import { ComponentProps, FC, useEffect, useState } from 'react';
import { PokerVariant } from '@/services/table/tableTypes';
import { motion } from 'framer-motion';
import { COLORS } from '@/constants/COLORS';
import { twMerge } from 'tailwind-merge';
import useIsMobileView from '@/hooks/useIsMobileView';

type MixGameRoundLabelProps = {
  round: number;
  roundsPerGame: number;
  currentGameMode: string;
  variants: PokerVariant[];
  showTransition: boolean;
} & ComponentProps<'div'>;

const MixGameRoundLabel: FC<MixGameRoundLabelProps> = ({
  currentGameMode,
  round,
  roundsPerGame,
  variants,
  showTransition,
}) => {
  const [currentVariantIndex, setCurrentVariantIndex] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const isMobileView = useIsMobileView();

  useEffect(() => {
    const currentIndexVariant = variants.findIndex((variant) => {
      return variant.name === currentGameMode;
    });
    if (currentIndexVariant !== -1) {
      setCurrentVariantIndex(currentIndexVariant);
    }
  }, [currentGameMode, variants]);

  useEffect(() => {
    if (showTransition && !isAnimating) {
      setAnimationKey((prev) => prev + 1);
      setIsAnimating(true);
    }
  }, [showTransition, currentVariantIndex, round, roundsPerGame]);

  const handleAnimationComplete = () => {
    setIsAnimating(false);
  };

  return (
    <div className="mt-4 flex flex-col items-center justify-center">
      <motion.div
        key={animationKey}
        className={twMerge(
          'flex h-full min-w-10 flex-col items-center justify-center rounded-md px-1',
          isMobileView && 'w-10',
        )}
        onAnimationComplete={handleAnimationComplete}
        style={{
          minWidth: '10em',
          backgroundColor: COLORS.primaryViolet,
          boxShadow: !isAnimating ? 'none' : `0 0 10px 2px ${COLORS.yellow}, 0 0 20px 4px ${COLORS.yellow}`,
        }}
        animate={
          isAnimating
            ? {
                boxShadow: [
                  'none',
                  `0 0 10px 2px ${COLORS.yellow}, 0 0 16px 4px ${COLORS.yellow}`,
                  'none',
                  `0 0 10px 2px ${COLORS.yellow}, 0 0 16px 4px ${COLORS.yellow}`,
                  'none',
                ],
              }
            : { boxShadow: 'none' }
        }
        transition={
          isAnimating
            ? {
                duration: 2,
                ease: 'easeInOut',
                times: [0, 0.2, 0.4, 0.8],
                repeat: 1,
                repeatType: 'loop',
                delay: 0.3,
              }
            : { duration: 0 }
        }
      >
        {currentGameMode && <p className="mt-0.5 pt-0.5 font-bold leading-4 text-ph-white">{currentGameMode}</p>}
        {round && roundsPerGame && <p className="text-ph-white">{`${round}/${roundsPerGame}`}</p>}
      </motion.div>
    </div>
  );
};

export default MixGameRoundLabel;
